import { Modal, ModalProp } from "../../components/modal/Modal";
import { Dialog } from "@headlessui/react";
import { ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { SetStateAction } from "react";
import { getExpirationDate, PointAdjustHistory, PtCategorySettings } from "../../model/PointData";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { convertDateToString, convertDateTimeToStringCompactFormat } from "../../utility/ConvertUtily";
import { Company } from "../../model/Companies";
import { requestGetPointAdjustHistory } from "../../api/Point.api";

export type PointAdjustmentListExportModalProps = {
  pointAdjustmentList: PointAdjustHistory[];
  setLoadProgress: (value: SetStateAction<number>) => void;
  company: Company | undefined;
  pointCategorySettingMap: Record<string, PtCategorySettings>;
};

export const PointAdjustmentListExportModal = ({
  open,
  toggle,
  closeOnClickOutside,
  pointAdjustmentList,
  setLoadProgress,
  company,
  pointCategorySettingMap,
}: ModalProp & PointAdjustmentListExportModalProps) => {

  const dispatch = useDispatch();

  const csvCol = [
    "登録日",
    "カテゴリ",
    "タイプ",
    "ポイント名",
    "有効期限",
    "ポイント発生日",
    "ポイント数",
    "備考",
    "登録者",
  ];

  // CSV出力処理
  async function exportCsvHandler () {
    toggle();
    setLoadProgress(0);
    const result = await exportCsv();
    setLoadProgress(-1);
    if(!result.result){
      console.log(result.details);
      dispatch(alertOpen(AlertValue(ConstMessage.ERR_CSV_EXPORT, "danger")));
    }
  };

  const exportCsv = async () => {
    try {
      const allAdjustments: PointAdjustHistory[] = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 5000;
      let totalCount = 0;

      while (hasMore) {
        const result = await requestGetPointAdjustHistory(page, pageSize);
        if (!result.success) {
          console.error("ポイント調整履歴取得に失敗", result.error);
          return { result: false, details: result.error };
        }
        if (page === 1) totalCount = result.totalCount ?? 0;

        // Firestoreカテゴリ設定がある場合に有効期限計算
        const mappedList = result.pointAdjustHistory.map((item) => {
          const categorySetting = pointCategorySettingMap[item.ptCategoryId];
          if (item.grantPoints && item.grantPoints > 0 && categorySetting) {
            item.expirationDate = getExpirationDate(categorySetting, item.occurrenceDate);
          }
          return item;
        });

        allAdjustments.push(...mappedList);

        hasMore = result.pointAdjustHistory.length === pageSize;

        const progressRate = Math.round((allAdjustments.length / totalCount) * 50);
        setLoadProgress(progressRate);

        page++;
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      // 作成日降順にソート
      const pointAdjustmentSortList = allAdjustments.sort((a, b) => {
        if (a.createAt < b.createAt) return 1;
        if (a.createAt > b.createAt) return -1;
        return 0;
      });

      const csvData = pointAdjustmentSortList.map((value) => [
        convertDateToString(value.createAt),
        value.ptCategoryName,
        value.ptTypeName,
        value.ptName,
        value.expirationDate ? convertDateToString(value.expirationDate) : "",
        convertDateToString(value.occurrenceDate),
        value.grantPoints,
        value.note,
        value.createUser,
      ]);
      // 文字列のエスケープ処理
      const escapeCsvData = csvData.map((row, index) => {
        setLoadProgress(50 + Math.round((index / csvData.length) * 50));
        return row.map((val) => `"${String(val).replace(/"/g, '""')}"`);
      });
      const escapeCsvCol = csvCol.map((value) => `"${value.replace(/"/g, '""')}"`);
      // カラムとデータを連結
      const csvStr = escapeCsvCol.join(',') + "\n" + escapeCsvData.map((row) => row.join(',')).join('\n');
      // CSVファイル名
      const fileName = `ポイント調整履歴_${company?.name}_${convertDateTimeToStringCompactFormat(new Date())}.csv`;
      const link = document.createElement("a");
      const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([bom, csvStr], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setLoadProgress(-1);
      return { result: true, details: undefined };
    } catch (err) {
      setLoadProgress(-1);
      return { result: false, details: err };
    }
  }

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full max-w-screen-md">
          <div className="px-2 pb-4 sm:p-4">
            <div className="text-center sm:mt-3">
              <Dialog.Title as="h3" className="text-left text-lg font-medium leading-6 text-gray-900">
                ポイント調整履歴出力確認
              </Dialog.Title>
            </div>
            <div className="mt-5">
              ポイント調整履歴の情報をCSVファイルで出力します。<br />
              <div className="text-red-500">
                ※CSV出力は、対象データの件数によって<span className="font-semibold">作業時間が長時間になる場合があります。</span><br/>
                ※一度実行すると中止することはできません。
              </div>
            </div>
          </div>
          <ModalFoterBox>
            <Button
              className="mx-2 mt-1 w-[132px]"
              outline
              onClick={toggle}
              children={"キャンセル"}
            />
            <Button
              className="mt-1 mr-2 w-[132px]"
              onClick={exportCsvHandler}
              children={"OK"}
            />
          </ModalFoterBox>
        </Dialog.Panel>
      </Modal>
    </>
  );  
}
  